// Global state and materials
import { LABELS, POLYGONS, VERTICES } from "../Global/variables";
import { COLORS } from "../Global/materials";

// Obstacle and panel utilities
import { deselectObstacle } from "../Obstacles/ObstacleFunctions";
import { disableObstacleVertexDrag } from "../Obstacles/ObstacleDragControls";
import { removeSegmentDragControls, setSegmentDragControls } from "./DragControls";
import { deselectPanels } from "../Panels/PanelFunctions";
import { sceneManager } from "../SetUps/SceneManager";
import { SEGMENT_LINE_WIDTH } from "../Global/fatLines";

// -------------------------------------------------------
// Segment hover highlight
// Mirrors the selected look (red fill) but lighter, with a wider, lighter border.
// -------------------------------------------------------
// Hover = blue theme (selected uses the yellow/gold theme).
const HOVER_FILL_COLOR = COLORS.hoverBlue;   // blue fill
const HOVER_FILL_OPACITY = 0.20;             // 20% fill on hover
const HOVER_BORDER_COLOR = COLORS.hoverBlue; // blue stroke
const HOVER_BORDER_WIDTH = SEGMENT_LINE_WIDTH; // thin border (same as base)

// Selected = yellow/gold theme (app accent).
// Border (outside) = deep yellow; inside fill = brighter yellow.
const SELECTED_FILL_COLOR = COLORS.selectedFillYellow;
const SELECTED_BORDER_COLOR = COLORS.selectedYellow;
const SELECTED_BORDER_WIDTH = SEGMENT_LINE_WIDTH * 2; // thicker border when selected
// Default (unselected, not hovered) = white stroke.
const BASE_BORDER_COLOR = COLORS.white;

let hoveredSegmentIndex = null;

function getSegmentWireframe(index) {
    const shape = POLYGONS[index]?.shape;
    if (!shape || !shape.children?.length) return null;
    return shape.children[shape.children.length - 1] || null;
}

function applySegmentHover(index) {
    const segment = POLYGONS[index];
    if (!segment?.shape?.material) return;
    const mat = segment.shape.material;
    mat.transparent = true;
    mat.opacity = HOVER_FILL_OPACITY;
    mat.color.setHex(HOVER_FILL_COLOR);

    const wf = getSegmentWireframe(index);
    if (wf?.material) {
        if (typeof wf.material.linewidth === "number") wf.material.linewidth = HOVER_BORDER_WIDTH;
        wf.material.color?.setHex(HOVER_BORDER_COLOR);
    }
}

function restoreSegmentBase(index) {
    const segment = POLYGONS[index];
    if (!segment?.shape?.material) return;
    const mat = segment.shape.material;
    mat.transparent = true;
    mat.opacity = 0.05;
    mat.color.setHex(COLORS.white);

    const wf = getSegmentWireframe(index);
    if (wf?.material) {
        if (typeof wf.material.linewidth === "number") wf.material.linewidth = SEGMENT_LINE_WIDTH;
        wf.material.color?.setHex(BASE_BORDER_COLOR);
    }
}

/**
 * Update which segment is hover-highlighted based on the cursor raycast.
 * The currently selected segment is skipped (keeps its selected styling).
 */
export function updateSegmentHover(raycaster, selectedSegmentIndex) {
    let hitIndex = null;
    for (let i = 0; i < POLYGONS.length; i++) {
        if (i === selectedSegmentIndex) continue;
        const shape = POLYGONS[i]?.shape;
        if (shape && raycaster.intersectObject(shape, false).length > 0) {
            hitIndex = i;
            break;
        }
    }

    if (hitIndex === hoveredSegmentIndex) return;

    if (hoveredSegmentIndex != null && hoveredSegmentIndex !== selectedSegmentIndex) {
        restoreSegmentBase(hoveredSegmentIndex);
    }
    hoveredSegmentIndex = hitIndex;
    if (hitIndex != null) applySegmentHover(hitIndex);
}

/** Clear any active hover highlight (without touching the selected segment). */
export function clearSegmentHover(selectedSegmentIndex = null) {
    if (hoveredSegmentIndex != null && hoveredSegmentIndex !== selectedSegmentIndex) {
        restoreSegmentBase(hoveredSegmentIndex);
    }
    hoveredSegmentIndex = null;
}

/**
 * Hover-highlight a segment directly by index (e.g. from the right-panel list).
 * Skips the currently selected segment so its styling is preserved.
 */
export function hoverSegmentByIndex(index, selectedSegmentIndex = null) {
    if (index == null || index === selectedSegmentIndex) {
        clearSegmentHover(selectedSegmentIndex);
        return;
    }
    if (index === hoveredSegmentIndex) return;
    if (hoveredSegmentIndex != null && hoveredSegmentIndex !== selectedSegmentIndex) {
        restoreSegmentBase(hoveredSegmentIndex);
    }
    hoveredSegmentIndex = index;
    applySegmentHover(index);
}

/**
 * Handles the selection of a segment (polygon area).
 * Deselects previously selected items and highlights the new selection.
 *
 * @param {object} selectedIndices - Current selected segment/obstacle/panel index.
 * @param {number} index - Index of the segment to select.
 * @param {function} setSelectedIndices - Recoil setter for selected state.
 * @param {function} setPolygonDetails - Setter to update the selected polygon's UI/metadata.
 */
export function selectSegment(selectedIndices, index, setSelectedIndices, setPolygonDetails) {
    let prev = selectedIndices.segmentIndex;
    sceneManager.controls.enabled = true; // ensure that orbit controls are enabled

    // Reset any hover styling so a hovered → selected segment shows the proper
    // selected look (not the lighter/wider hover border).
    clearSegmentHover();

    // Deselect obstacle if switching segments and one is selected
    if (prev != null && selectedIndices.obstacleIndex != null) {
        let selectedObs = POLYGONS[prev].obstructions[selectedIndices.obstacleIndex];
        deselectObstacle(selectedObs);
        disableObstacleVertexDrag();
    }

    // If the same segment is clicked again, deselect everything
    if (prev === index && prev != null) {
        deselectSegment(index);
        deselectPanels(selectedIndices.panelIndex, setSelectedIndices, selectedIndices);
        setSelectedIndices({ segmentIndex: null, obstacleIndex: null, panelIndex: null });
    }

    // Otherwise, highlight the new segment and set up its drag controls
    else if (setPolygonDetails != null) {
        if (prev !== null) deselectSegment(prev);
        segmentHighlight(index, {
            opacity: 0.35,
            color: SELECTED_FILL_COLOR,
            borderColor: SELECTED_BORDER_COLOR,
            borderWidth: SELECTED_BORDER_WIDTH,
            transparent: true,
            labelVisible: true,
            vertexOpacity: 1
        });

        setSelectedIndices({ segmentIndex: index, obstacleIndex: null, panelIndex: null });
        setSegmentDragControls(index, setPolygonDetails);
    }
}

/**
 * Deselect a polygon segment by resetting its visual appearance and controls.
 * 
 * @param {number} index - Segment index to deselect.
 */
export function deselectSegment(index) {
    segmentHighlight(index, {
        opacity: 0.05,
        color: COLORS.white,
        borderColor: BASE_BORDER_COLOR,
        transparent: true,
        labelVisible: false,
        vertexOpacity: 0
    });

    removeSegmentDragControls();
}

/**
 * Show or hide all measurement labels on segments.
 * 
 * @param {boolean} showLabels - True to show, false to hide.
 */
export function toggleSegmentLabels(showLabels) {
    for (let i = 0; i < POLYGONS.length; i++) {
        let segment = POLYGONS[i];
        for (let edge of segment.edges) {
            LABELS.measurements[edge.id].visible = showLabels;
        }
    }
}

/**
 * Show only the labels on the currently selected segment.
 * 
 * @param {number} index - Index of the segment to show labels for.
 */
export function showSelectedSegmentLabel(index) {
    let segment = POLYGONS[index];
    for (let edge of segment.edges) {
        LABELS.measurements[edge.id].visible = true;
    }
}

/**
 * Helper function to visually highlight a polygon segment.
 * Updates material, label visibility, and vertex opacity.
 * 
 * @param {number} index - Segment index.
 * @param {object} options - Highlight styling options:
 *   - opacity: material transparency level
 *   - color: material color (hex)
 *   - transparent: boolean for transparency
 *   - labelVisible: whether measurement labels are shown
 *   - vertexOpacity: vertex point opacity
 */
function segmentHighlight(index, options) {
    const segment = POLYGONS[index];

    // Update material
    segment.shape.material.opacity = options.opacity || 0.2;
    segment.shape.material.color.setHex(options.color || 0xffffff);
    segment.shape.material.transparent = options.transparent || false;

    // Update border (wireframe) color / width
    const wf = getSegmentWireframe(index);
    if (wf?.material) {
        if (options.borderColor != null) wf.material.color?.setHex(options.borderColor);
        if (typeof wf.material.linewidth === "number") {
            wf.material.linewidth = options.borderWidth || SEGMENT_LINE_WIDTH;
        }
    }

    // Toggle edge label visibility
    for (let edge of segment.edges) {
        LABELS.measurements[edge.id].visible = options.labelVisible || false;
    }

    // Adjust vertex point visibility
    VERTICES[index].forEach(vertex => {
        vertex.material.opacity = options.vertexOpacity || 0;
    });
}
